import type { BattleMeta, BattleStatus, Item, QuestionSet } from './types';
import { botArrivals, lobbyCount } from './bots';
import { withVoidMark } from './scoring';

/** The intro card before a question opens; phones see it after realtime delivery. */
export const PRE_ROLL_MS = 3000;
/** v2.2: themed rounds. A round's first question shows its round card (name and rule) for this long instead of PRE_ROLL_MS. */
export const ROUND_CARD_MS = 5000;
/** Answers stamped up to this long after the deadline still count. */
export const GRACE_MS = 500;
/**
 * A question may be closed this long after its deadline. An answer stamped inside the grace still has to be written by
 * its function (a cold start or a slow read can take a second or more), and the close must not read the shards before
 * then: so the grace plus 1.5 s. The autopilot (planNext, runDue), POST /close and every phone's due times use this.
 */
export const CLOSE_AFTER_MS = 2000;
/** The answer view after a close, then the leaderboard view. Battles without config use these too. */
export const DEFAULT_REVEAL_MS = 7000;
export const DEFAULT_BOARD_MS = 6000;
/** Diagnose in five: a clue is dealt this often unless the item says otherwise. */
export const DEFAULT_CLUE_INTERVAL_MS = 5000;
/** When the engine refuses work the planner said was due (an empty set, say), the autopilot looks again this often. A hold no longer polls (30 Oct 2026). */
export const HOLD_POLL_MS = 2000;
/** 17 Oct 2026 (presenter console): the stream delay. On air runs this far ahead of the phones. */
export const MAX_DELAY_MS = 15_000;
export const DELAY_STEP_MS = 500;
/** A battle's stream delay: 0 to MAX_DELAY_MS in DELAY_STEP_MS steps; anything else reads as 0. */
export function delayOf(m: Pick<BattleMeta, 'delayMs'>): number {
  const d = m.delayMs;
  if (typeof d !== 'number' || !Number.isFinite(d) || d <= 0) return 0;
  return Math.min(MAX_DELAY_MS, Math.round(d / DELAY_STEP_MS) * DELAY_STEP_MS);
}

export type EngineResult = { ok: true; meta: BattleMeta } | { ok: false; reason: string };
const fail = (reason: string): EngineResult => ({ ok: false, reason });
const bump = (m: BattleMeta, patch: Partial<BattleMeta>): EngineResult => ({ ok: true, meta: { ...m, ...patch, version: m.version + 1 } });

type Configured = Pick<BattleMeta, 'config'>;
const nonNegative = (n: unknown): number | undefined => (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : undefined);

/** Battles without config are manual, as every battle was on Day 1. */
export const modeOf = (m: Configured): 'autopilot' | 'manual' => (m.config?.mode === 'autopilot' ? 'autopilot' : 'manual');
export const isAutopilot = (m: Configured) => modeOf(m) === 'autopilot';
export const revealMsOf = (m: Configured) => nonNegative(m.config?.revealMs) ?? DEFAULT_REVEAL_MS;
export const boardMsOf = (m: Configured) => nonNegative(m.config?.boardMs) ?? DEFAULT_BOARD_MS;

/**
 * How long a question's intro runs before it opens: the round card (ROUND_CARD_MS) for the first question of a themed
 * round, otherwise PRE_ROLL_MS. Every transition that opens a question uses it, so startsAt (and with it every clue,
 * the deadline and the close that planNext schedules) already includes it. Phones and the stage time their intro the
 * same way, from question.startsAt.
 */
export const preRollFor = (item: Pick<Item, 'round'> | undefined): number => (item?.round?.first === true ? ROUND_CARD_MS : PRE_ROLL_MS);

// ---------------------------------------------------------------------------
// Clues ('clues' format): clue k (from 0) falls due at startsAt + k × interval. A clue whose time is at or after
// the deadline is never dealt, and nothing is dealt before the question starts.
// ---------------------------------------------------------------------------
type ClueItem = Pick<Item, 'format' | 'clues' | 'clueIntervalMs'>;
export const clueCount = (item: ClueItem): number => (item.format === 'clues' && Array.isArray(item.clues) ? item.clues.length : 0);
export const clueIntervalOf = (item: Pick<Item, 'clueIntervalMs'>): number =>
  typeof item.clueIntervalMs === 'number' && item.clueIntervalMs > 0 ? item.clueIntervalMs : DEFAULT_CLUE_INTERVAL_MS;

/** How many clues have fallen due at `now`. */
export function cluesDueAt(item: ClueItem, startsAt: number, deadlineAt: number, now: number): number {
  const n = clueCount(item);
  if (n === 0 || now < startsAt || deadlineAt <= startsAt) return 0;
  const every = clueIntervalOf(item);
  const byNow = Math.floor((now - startsAt) / every) + 1;
  const beforeDeadline = Math.ceil((deadlineAt - startsAt) / every);
  return Math.max(0, Math.min(n, byNow, beforeDeadline));
}

/** The clues a snapshot of the open question may carry: the ones whose time has come, or the ones already released if more. */
export function cluesVisible(m: Pick<BattleMeta, 'startsAt' | 'deadlineAt' | 'cluesShown'>, item: ClueItem, now: number): number {
  const n = clueCount(item);
  if (n === 0 || m.startsAt === undefined || m.deadlineAt === undefined) return 0;
  return Math.min(n, Math.max(m.cluesShown ?? 0, cluesDueAt(item, m.startsAt, m.deadlineAt, now)));
}

/** When the next clue of the open question falls due, if one is still to come. */
export function nextClueAt(m: Pick<BattleMeta, 'startsAt' | 'deadlineAt' | 'cluesShown'>, item: ClueItem): number | undefined {
  const n = clueCount(item);
  if (n === 0 || m.startsAt === undefined || m.deadlineAt === undefined) return undefined;
  const k = Math.max(0, m.cluesShown ?? 0);
  if (k >= n) return undefined;
  const at = m.startsAt + k * clueIntervalOf(item);
  return at < m.deadlineAt ? at : undefined;
}

// ---------------------------------------------------------------------------
// Transitions. Each is pure: the meta before, the time, the meta after with the version bumped (or a reason not to).
// ---------------------------------------------------------------------------
/**
 * Opens question `index` after its pre-roll. With a stream delay (17 Oct 2026) the phones' intro starts the delay later,
 * so startsAt and the deadline move by it, and the question's text stays off the public snapshot until unveilAt, the
 * moment the intro would have started without the delay (the 'unveil' due action releases it).
 */
function openQuestion(m: BattleMeta, set: QuestionSet, index: number, now: number, extra: Partial<BattleMeta> = {}): EngineResult {
  const item = set.items[index];
  if (!item) return fail('no such question');
  const pre = preRollFor(item);
  const delay = delayOf(m);
  const startsAt = now + pre + delay;
  return bump(m, {
    ...extra, status: 'question', currentIndex: index, startsAt, deadlineAt: startsAt + item.durationMs,
    revealUntil: undefined, cluesShown: clueCount(item) > 0 ? 0 : undefined,
    unveilAt: delay > 0 ? startsAt - pre : undefined, unveiled: undefined, closedAt: undefined,
  });
}

/** Opens the first question after its pre-roll (the round card in a themed battle). Pressing start is a decision to go, so it also lifts a hold. */
export function start(m: BattleMeta, set: QuestionSet, now: number): EngineResult {
  if (m.status !== 'lobby') return fail('battle already started');
  if (set.items.length === 0) return fail('question set is empty');
  return openQuestion(m, set, 0, now, { hold: undefined });
}

/** Moves the open question to its reveal, no earlier than CLOSE_AFTER_MS after the deadline, and stamps when. On autopilot it also sets when the next question opens. */
export function closeQuestion(m: BattleMeta, now: number): EngineResult {
  if (m.status !== 'question') return fail('no open question');
  if (m.deadlineAt === undefined || now < m.deadlineAt + CLOSE_AFTER_MS) return fail('too early to close');
  return bump(m, { status: 'reveal', revealUntil: isAutopilot(m) ? now + revealMsOf(m) + boardMsOf(m) : undefined, closedAt: now });
}

/**
 * Opens the following question, or the podium after the last one. Allowed in a reveal: the producer's Next in manual
 * mode, an override on autopilot, and what the autopilot itself calls once the reveal and leaderboard have run.
 * A hold survives it, so a held battle can be stepped through one question at a time.
 */
export function next(m: BattleMeta, set: QuestionSet, now: number): EngineResult {
  if (m.status !== 'reveal') return fail('next is only allowed after a reveal');
  const index = m.currentIndex + 1;
  if (index >= set.items.length) {
    return bump(m, { status: 'podium', startsAt: undefined, deadlineAt: undefined, revealUntil: undefined, cluesShown: undefined, hold: undefined, endedAt: m.endedAt ?? now });
  }
  return openQuestion(m, set, index, now);
}

/** `list` with `index` added once, in order. */
const withIndex = (list: readonly number[] | undefined, index: number): number[] =>
  [...new Set([...(Array.isArray(list) ? list : []), index])].sort((a, b) => a - b);

/**
 * Ends the open question now, and it scores nothing for anyone (spec section 5): its index joins meta.voided. It never
 * moves a deadline later, so it cannot reopen the answer window. Its scoring mark in meta.bonus is written at its close
 * (see VOID_MARK in @shared/scoring), which still follows after CLOSE_AFTER_MS.
 */
export function skip(m: BattleMeta, now: number): EngineResult {
  if (m.status !== 'question') return fail('no open question to skip');
  if (m.deadlineAt !== undefined && now >= m.deadlineAt) return fail('the time is already up');
  return bump(m, { deadlineAt: now, voided: withIndex(m.voided, m.currentIndex) });
}

/**
 * Ends the battle from anywhere: the podium shows the scores so far. A question it cuts short never closes, so the
 * leaderboard that stands is the one before it; it is voided (scores nothing) so every score shown agrees with that board.
 */
export function end(m: BattleMeta, now: number): EngineResult {
  if (m.status === 'ended') return fail('already ended');
  const cut = m.status === 'question' && m.currentIndex >= 0 ? { voided: withIndex(m.voided, m.currentIndex), bonus: withVoidMark(m.bonus, m.currentIndex) } : {};
  return bump(m, { ...cut, status: 'ended', startsAt: undefined, deadlineAt: undefined, revealUntil: undefined, cluesShown: undefined, hold: undefined, endedAt: m.endedAt ?? now });
}

/** The last question to have closed: the current one from its reveal on, the one before it while a question is open, -1 before any. */
export function lastClosedIndex(m: Pick<BattleMeta, 'status' | 'currentIndex'>): number {
  if (m.status === 'question') return m.currentIndex - 1;
  if (m.status === 'reveal' || m.status === 'podium' || m.status === 'ended') return m.currentIndex;
  return -1;
}

/**
 * Voids a closed question (a wrongly keyed one, say): it scores nothing for anyone, from now on in every score shown.
 * Any question up to the last one closed may be voided, once; the open question is skipped instead. It joins
 * meta.voided and gets its scoring mark in meta.bonus (in place of any rarity points it paid).
 */
export function voidQuestion(m: BattleMeta, set: QuestionSet, index: number = lastClosedIndex(m)): EngineResult {
  const last = lastClosedIndex(m);
  if (last < 0) return fail('no question has closed yet');
  if (!Number.isInteger(index) || index < 0 || index >= set.items.length) return fail('no such question');
  if (m.status === 'question' && index === m.currentIndex) return fail('that question is still open, skip it instead');
  if (index > last) return fail('that question has not closed yet');
  if (Array.isArray(m.voided) && m.voided.includes(index)) return fail('that question is already void');
  return bump(m, { voided: withIndex(m.voided, index), bonus: withVoidMark(m.bonus, index) });
}

const HOLDABLE: readonly BattleStatus[] = ['lobby', 'question', 'reveal'];

/**
 * The producer pauses the autopilot. In the lobby it stops a scheduled start; during a question the question still
 * runs and closes, and the battle stops after its reveal; in a reveal the next question waits. Resume lifts it.
 */
export function hold(m: BattleMeta, _now?: number): EngineResult {
  if (!HOLDABLE.includes(m.status)) return fail('the battle is over');
  if (m.hold) return fail('already on hold');
  return bump(m, { hold: true });
}

export function resume(m: BattleMeta, _now?: number): EngineResult {
  if (!m.hold) return fail('not on hold');
  return bump(m, { hold: undefined });
}

/** 17 Oct 2026: releases the open question's text to the public snapshot (a delayed battle only), at unveilAt. */
export function unveil(m: BattleMeta, now: number): EngineResult {
  if (m.status !== 'question' || m.unveilAt === undefined) return fail('nothing to unveil');
  if (m.unveiled) return fail('already unveiled');
  if (now < m.unveilAt) return fail('too early to unveil');
  return bump(m, { unveiled: true });
}

/**
 * Sets, moves or (with null) clears the start time of an autopilot battle still in the lobby (18 Oct 2026: the host
 * page's Start at). A new time also lifts a hold on the start, so the countdown runs to it. The caller checks the time.
 */
export function schedule(m: BattleMeta, at: number | null): EngineResult {
  if (m.status !== 'lobby') return fail('the battle has already started');
  if (!isAutopilot(m) || !m.config) return fail('only a battle on autopilot starts by itself');
  const { scheduledStartAt: _old, ...config } = m.config;
  return bump(m, { config: at === null ? config : { ...config, scheduledStartAt: at }, hold: undefined });
}

/** Releases every clue of the open question whose time has come (more than one if the server was late). */
export function releaseClues(m: BattleMeta, set: QuestionSet, now: number): EngineResult {
  if (m.status !== 'question' || m.startsAt === undefined || m.deadlineAt === undefined) return fail('no open question');
  const item = set.items[m.currentIndex];
  if (!item || clueCount(item) === 0) return fail('this question has no clues');
  const due = cluesDueAt(item, m.startsAt, m.deadlineAt, now);
  if (due <= (m.cluesShown ?? 0)) return fail('no clue is due');
  return bump(m, { cluesShown: due });
}

// ---------------------------------------------------------------------------
// The autopilot's planner: what falls due next, and when.
// ---------------------------------------------------------------------------
export type DueAction = 'start' | 'unveil' | 'clue' | 'close' | 'next' | 'bots' | 'none';
/**
 * `at` is when the action falls due (at or before `now` means due now). For 'none', `at` is when to look again.
 * `done` means nothing more will fall due on the server until someone acts (or the battle is over).
 */
export interface DuePlan { action: DueAction; at?: number; done: boolean }

/**
 * Pure: what is due for this battle and when.
 * - Lobby: an autopilot battle with a scheduled start starts at that time unless held; otherwise nothing until the producer starts it.
 * - Question (any mode): in a delayed battle the unveil at unveilAt first (17 Oct 2026), unless the question was skipped
 *   (a skipped question is never unveiled: it closes CLOSE_AFTER_MS after the skip instead of waiting for unveilAt); then
 *   the next clue of a 'clues' item, else the close at deadline + CLOSE_AFTER_MS. Both follow the stamped startsAt and
 *   deadlineAt, so a round opener's longer pre-roll (preRollFor) and the stream delay move them too.
 * - Reveal: on autopilot the next question (or the podium) at revealUntil unless held; a held reveal is done (30 Oct 2026, below).
 *   Manual battles never move on by themselves.
 * - Podium and ended: done.
 * - 30 Oct 2026: a hold no longer looks again in HOLD_POLL_MS. A held start or a held reveal is done; resume, a new start
 *   time or Next while held arms a fresh run. Lobby bots still arrive while the start is held.
 */
export function planNext(m: BattleMeta, set: QuestionSet, now: number): DuePlan {
  switch (m.status) {
    case 'lobby': {
      // 29 Oct 2026: bots arrive in the lobby on their own schedule (shared/bots), before the start, manual battles too.
      const botAt = nextBotArrivalAt(m);
      const at = m.config?.scheduledStartAt;
      // 30 Oct 2026: a held start is not looked at again; resume or a new start time arms a fresh run.
      const startPlan: DuePlan | undefined = !isAutopilot(m) || typeof at !== 'number' || m.hold ? undefined : { action: 'start', at, done: false };
      if (botAt !== undefined && (startPlan?.at === undefined || botAt < startPlan.at)) return { action: 'bots', at: botAt, done: false };
      return startPlan ?? { action: 'none', done: true };
    }
    case 'question': {
      const item = set.items[m.currentIndex];
      if (!item || m.deadlineAt === undefined) return { action: 'none', done: true };
      const skipped = Array.isArray(m.voided) && m.voided.includes(m.currentIndex);
      if (m.unveilAt !== undefined && !m.unveiled && !skipped) return { action: 'unveil', at: m.unveilAt, done: false };
      const clueAt = nextClueAt(m, item);
      if (clueAt !== undefined) return { action: 'clue', at: clueAt, done: false };
      return { action: 'close', at: m.deadlineAt + CLOSE_AFTER_MS, done: false };
    }
    case 'reveal': {
      if (!isAutopilot(m)) return { action: 'none', done: true };
      if (m.hold) return { action: 'none', done: true };
      const at = m.revealUntil ?? (m.deadlineAt !== undefined ? m.deadlineAt + CLOSE_AFTER_MS + revealMsOf(m) + boardMsOf(m) : now);
      return { action: 'next', at, done: false };
    }
    default:
      return { action: 'none', done: true };
  }
}

/** 29 Oct 2026: when the next lobby bot arrives (none once every lobby bot is seated, or the battle has no bots). */
export function nextBotArrivalAt(m: Pick<BattleMeta, 'config' | 'createdAt' | 'botsSeated'>): number | undefined {
  const cfg = m.config?.bots; if (!cfg) return undefined;
  const arrivals = botArrivals(cfg, m.createdAt, m.config?.scheduledStartAt);
  const seated = m.botsSeated ?? 0;
  if (seated >= lobbyCount(arrivals)) return undefined;
  const a = arrivals[seated];
  return a.kind === 'lobby' ? a.at : undefined;
}
